/**
 * Authentication and authorization context.
 *
 * The permission decisions here are convenience, not security. `can()` from
 * `@rescure/shared` is the same function the Pages Functions call, so the UI and the
 * server agree about what a role may do — but the UI's copy exists only to avoid
 * rendering a button that would fail. `_lib/authz.ts` on the server is the actual
 * boundary. Anyone reading this file looking for the security model should read that
 * file instead.
 *
 * Session state is a cookie the server sets (`Set-Cookie` on sign-in/sign-up) and
 * clears (on sign-out) — there is no client-held token. `GET /api/auth/session` is
 * the "who am I" bootstrap call on load, and a 401 from any other call clears local
 * state via the `auth:unauthenticated` window event `api.ts` dispatches.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  ROLE_HOME,
  can,
  inTenant,
  seesIdentifiedData,
  type Permission,
  type Role,
  type UserProfile,
} from '@rescure/shared'
import { api, ApiError, AUTH_UNAUTHENTICATED_EVENT } from './api'

/** Minimal stand-in for the old Firebase `User` object — just enough for the few call
 * sites that read `user?.uid` as an identity handle. */
interface SessionUser {
  uid: string
}

interface AuthState {
  /** Signed-in identity handle, or `null` when signed out. */
  user: SessionUser | null
  /** The profile row, which is the source of truth for role and tenancy. */
  profile: UserProfile | null
  /**
   * `true` until the first session check resolves.
   *
   * Distinguished from "signed out" because conflating them causes a visible flash of
   * the sign-in screen on every reload for an already-authenticated user — and on a
   * clinical tool that flash reads as "it logged me out again".
   */
  loading: boolean
  error: string | null
  signIn: (email: string, password: string) => Promise<UserProfile>
  signOut: () => Promise<void>
  /** Permission check, delegating to the shared matrix. */
  can: (permission: Permission) => boolean
  /** Tenancy check: is this org's data ours to touch? */
  inTenant: (orgId: string | undefined) => boolean
  /** Whether this role may see names and verbatim chart quotes. */
  seesIdentified: boolean
  role: Role | null
  /** Where this role's home route is. */
  home: string
}

const AuthContext = createContext<AuthState | null>(null)

function toSessionUser(profile: UserProfile | null): SessionUser | null {
  return profile ? { uid: String(profile.id) } : null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Bootstrap: ask the server who the current cookie belongs to.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const result = await api.get<{ profile: UserProfile | null }>('/api/auth/session')
        if (!cancelled) setProfile(result.profile)
      } catch (cause) {
        if (!cancelled) {
          setProfile(null)
          if (!(cause instanceof ApiError && cause.code === 'UNAUTHENTICATED')) {
            setError(cause instanceof Error ? cause.message : 'Could not load your session.')
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // A 401 from any other call means the cookie is gone or expired — clear local state
  // to match, rather than letting the UI keep believing it is signed in.
  useEffect(() => {
    function onUnauthenticated() {
      setProfile(null)
      queryClient.clear()
    }
    window.addEventListener(AUTH_UNAUTHENTICATED_EVENT, onUnauthenticated)
    return () => window.removeEventListener(AUTH_UNAUTHENTICATED_EVENT, onUnauthenticated)
  }, [queryClient])

  const value = useMemo<AuthState>(() => {
    const role = profile?.role ?? null

    return {
      user: toSessionUser(profile),
      profile,
      loading,
      error,

      signIn: async (email, password) => {
        setError(null)
        try {
          const result = await api.post<{ profile: UserProfile }>('/api/auth/signin', { email, password })
          // Different accounts can be permission-filtered differently for the same
          // resource id, so any cache from a previous account must not survive a
          // switch — clear before adopting the new profile, not after.
          queryClient.clear()
          setProfile(result.profile)
          return result.profile
        } catch (cause) {
          const message =
            cause instanceof ApiError && cause.code === 'UNAUTHENTICATED'
              ? 'That email and password do not match an account.'
              : cause instanceof Error
                ? cause.message
                : 'Sign-in failed.'
          setError(message)
          throw cause
        }
      },

      signOut: async () => {
        try {
          await api.post('/api/auth/signout')
        } finally {
          setProfile(null)
          queryClient.clear()
        }
      },

      // A user with no profile has no permissions at all, rather than defaulting to
      // some baseline. Failing closed is the only safe direction here.
      can: (permission) =>
        profile
          ? can(
              {
                role: profile.role,
                disabled: profile.disabled ?? false,
                hasHumanSubjectsTraining: profile.hasHumanSubjectsTraining ?? false,
              },
              permission
            )
          : false,

      inTenant: (targetOrgId) =>
        profile ? inTenant({ role: profile.role, orgId: profile.orgId }, targetOrgId) : false,

      seesIdentified: role ? seesIdentifiedData(role) : false,
      role,
      home: role ? ROLE_HOME[role] : '/signin',
    }
  }, [profile, loading, error, queryClient])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>.')
  }
  return context
}

/**
 * Read a permission without pulling in the whole auth object.
 * Keeps `can('candidate:approve_outreach')` readable at the call site.
 */
export function usePermission(permission: Permission): boolean {
  return useAuth().can(permission)
}
